// src/pages/talentScout/getPlayer.js

import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ProfileImage from "../../components/profileImage";

const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1";
const APP_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1";

const GetPlayer = () => {
  const { t } = useTranslation();
  const [player, setPlayer] = useState(null);
  const [team, setTeam] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [performances, setPerformances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [parents, setParents] = useState([]);
  const [parentsLoading, setParentsLoading] = useState(false);

  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPlayerData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("Authentication token not found");
        }

        console.log("Fetching player data for ID:", id); // Debug log

        // Fetch player details
        const playerResponse = await fetch(
          `${API_URL}/talent-scout/getPlayerById?id=${id}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!playerResponse.ok) {
          const errorData = await playerResponse.json();
          throw new Error(
            errorData.message ||
              `Failed to fetch player data: ${playerResponse.status}`
          );
        }

        const playerData = await playerResponse.json();
        if (!playerData.player) {
          throw new Error("Player data not found");
        }

        setPlayer(playerData.player);
        setTeam(playerData.team);
        setOrganization(playerData.organization);

        // Fetch performance data
        const performanceResponse = await fetch(
          `${API_URL}/talent-scout/getPlayerPerformanceData?id=${id}&page=${currentPage}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!performanceResponse.ok) {
          const errorData = await performanceResponse.json();
          throw new Error(
            errorData.message ||
              `Failed to fetch performance data: ${performanceResponse.status}`
          );
        }

        const performanceData = await performanceResponse.json();
        setPerformances(performanceData.performance || []);
        setTotalPages(performanceData.totalPages || 0);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError(error.message || "An unexpected error occurred");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      console.log("Starting fetch with ID:", id); // Debug log
      fetchPlayerData();
    } else {
      console.log("No ID provided"); // Debug log
      setError("Player ID is required");
      setLoading(false);
    }
  }, [id, currentPage]);

  useEffect(() => {
    const fetchParents = async () => {
      if (activeTab === "parents" && id) {
        setParentsLoading(true);
        try {
          const token = localStorage.getItem("token");
          const response = await fetch(
            `${API_URL}/talent-scout/getAllParentsOfPlayer?id=${id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (!response.ok) {
            throw new Error("Failed to fetch parents data");
          }

          const data = await response.json();
          setParents(data);
        } catch (error) {
          console.error("Error fetching parents:", error);
        } finally {
          setParentsLoading(false);
        }
      }
    };

    fetchParents();
  }, [id, activeTab]);

  const getPlayerAge = () => {
    if (!player?.metaData?.birthday) return "N/A";
    const birth = new Date(player.metaData.birthday);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const getAccountAge = () => {
    if (!player?.createdAt) return "N/A";
    const created = new Date(player.createdAt);
    const now = new Date();
    const diffTime = Math.abs(now - created);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 30) return `${diffDays} days`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months`;
    return `${Math.floor(diffDays / 365)} years`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-orange-50 to-red-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-orange-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">{t("talentScout.loadingPlayerProfile")}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-red-50 to-pink-100">
        <div className="text-center bg-white p-8 rounded-xl shadow-lg">
          <div className="text-red-500 text-xl font-semibold mb-2">⚠️ {t("talentScout.error")}</div>
          <div className="text-gray-600 mb-4">{error}</div>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium"
          >
            ← {t("talentScout.goBack")}
          </button>
        </div>
      </div>
    );
  }

  if (!player) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center bg-white p-8 rounded-xl shadow-lg">
          <div className="text-xl font-semibold mb-2">👤 Player Not Found</div>
          <div className="text-gray-600 mb-4">{t("talentScout.requestedPlayerNotFound")}</div>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium"
          >
            ← {t("talentScout.goBack")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center text-gray-600 hover:text-gray-900 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {t("talentScout.backToSearch")}
        </button>

        {/* Hero Section */}
        <div className="bg-gradient-to-r from-orange-600 to-red-600 rounded-2xl shadow-xl overflow-hidden mb-8" style={{background: "linear-gradient(135deg, #EC6B37 0%, #d55a2e 100%)"}}>
          <div className="p-8 text-white">
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-8">
              <div className="flex-shrink-0">
                <ProfileImage
                  image={player?.profilePicture}
                  alt={player?.firstName}
                  myUser={player}
                  className="w-32 h-32 rounded-2xl object-cover ring-4 ring-white/20 shadow-xl"
                />
              </div>
              <div className="flex-grow">
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                  <div>
                    <h1 className="text-4xl font-bold mb-2">
                      {player?.firstName} {player?.lastName}
                    </h1>
                    <p className="text-orange-100 text-lg mb-4">@{player?.userName}</p>
                    <div className="flex flex-wrap gap-3">
                      <span className="bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full text-sm">
                        👤 {getPlayerAge()} years old
                      </span>
                      <span className="bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full text-sm">
                        ⚧ {player?.gender}
                      </span>
                      <span className="bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full text-sm">
                        📧 {player?.email}
                      </span>
                      <span className="bg-emerald-500/20 backdrop-blur-sm px-3 py-1 rounded-full text-sm">
                        ✓ {player?.accountType}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Link
                      to={`/feed/${player?._id}`}
                      className="px-6 py-3 bg-white text-orange-600 rounded-lg font-semibold hover:bg-orange-50 transition-colors flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                      </svg>
                      {t("talentScout.viewFeed")}
                    </Link>
                    <Link
                      to={`/chat/${player?._id}`}
                      className="px-6 py-3 bg-emerald-500 text-white rounded-lg font-semibold hover:bg-emerald-600 transition-colors flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                      </svg>
                      {t("talentScout.startChat")}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Age</p>
                <p className="text-3xl font-bold text-gray-900">{getPlayerAge()}</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1 1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">{t("talentScout.accountAge")}</p>
                <p className="text-3xl font-bold text-gray-900">{getAccountAge()}</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">{t("talentScout.performance")}</p>
                <p className="text-3xl font-bold text-gray-900">{performances.length}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">{t("talentScout.status")}</p>
                <p className="text-xl font-bold text-green-600">{t("talentScout.active")}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6 overflow-x-auto">
              {["overview", "performance", "team", "organization", "parents"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                    activeTab === tab
                      ? "border-orange-500 text-orange-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {t(`talentScout.${tab}`)}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === "overview" && (
              <div className="space-y-8">
                {/* Player Details */}
                <div>
                  <h3 className="text-xl font-semibold mb-4">{t("talentScout.playerInformation")}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 font-medium">{t("talentScout.fullName")}</p>
                      <p className="text-lg font-semibold text-gray-900">{player?.firstName} {player?.lastName}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 font-medium">{t("talentScout.username")}</p>
                      <p className="text-lg font-semibold text-gray-900">@{player?.userName}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 font-medium">Email</p>
                      <p className="text-lg font-semibold text-gray-900">{player?.email}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 font-medium">Phone</p>
                      <p className="text-lg font-semibold text-gray-900">{player?.phone || t("talentScout.notProvided")}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 font-medium">{t("talentScout.age")}</p>
                      <p className="text-lg font-semibold text-gray-900">{getPlayerAge()} years</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 font-medium">Gender</p>
                      <p className="text-lg font-semibold text-gray-900 capitalize">{player?.gender}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 font-medium">{t("talentScout.accountType")}</p>
                      <p className="text-lg font-semibold text-gray-900 capitalize">{player?.accountType}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 font-medium">{t("talentScout.accountStatus")}</p>
                      <p className="text-lg font-semibold text-green-600">{t("talentScout.active")}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 font-medium">{t("talentScout.memberSince")}</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {new Date(player?.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Player Analytics */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-xl">
                    <h4 className="font-semibold text-orange-900 mb-4">{t("talentScout.playerStatistics")}</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-orange-700">{t("talentScout.performanceRecords")}:</span>
                        <span className="font-medium text-orange-900">{performances.length}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-orange-700">{t("talentScout.accountAge")}:</span>
                        <span className="font-medium text-orange-900">{getAccountAge()}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-orange-700">{t("talentScout.profileStatus")}:</span>
                        <span className="font-medium text-orange-900">{player?.profilePicture ? t("talentScout.complete") : t("talentScout.incomplete")}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 p-6 rounded-xl">
                    <h4 className="font-semibold text-emerald-900 mb-4">{t("talentScout.accountHealth")}</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-emerald-700">{t("talentScout.emailVerified")}:</span>
                        <span className="font-medium text-emerald-900">{player?.isAccountVerified ? t("talentScout.yes") : t("talentScout.no")}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-emerald-700">{t("talentScout.phoneVerified")}:</span>
                        <span className="font-medium text-emerald-900">{player?.isPhoneVerified ? t("talentScout.yes") : t("talentScout.no")}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-emerald-700">{t("talentScout.lastActive")}:</span>
                        <span className="font-medium text-emerald-900">
                          {player?.metaData?.lastActive ? new Date(player.metaData.lastActive).toLocaleDateString() : t("talentScout.unknown")}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Performance Tab */}
            {activeTab === "performance" && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-semibold">Performance Records</h3>
                  <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                    {performances.length} records
                  </span>
                </div>

                {performances.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <p className="text-gray-500 text-lg">No performance records found</p>
                    <p className="text-gray-400 text-sm">This player hasn't uploaded any performance data yet.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {performances.map((performance, index) => (
                      <div key={index} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                        <div className="flex justify-between items-start mb-4">
                          <h4 className="font-semibold text-gray-900">Performance #{index + 1}</h4>
                          <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium">
                            {new Date(performance.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        {performance.document && (
                          <a
                            href={`${APP_URL}${performance.document}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-orange-600 hover:text-orange-700 text-sm font-medium"
                          >
                            📄 View Document
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center space-x-2 mt-8">
                    {Array.from({ length: totalPages }, (_, index) => (
                      <button
                        key={index + 1}
                        onClick={() => setCurrentPage(index + 1)}
                        className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                          currentPage === index + 1
                            ? "bg-orange-500 text-white"
                            : "text-gray-500 hover:bg-gray-100"
                        }`}
                      >
                        {index + 1}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Team Tab */}
            {activeTab === "team" && (
              <div className="space-y-6">
                {team ? (
                  <div className="bg-white border border-gray-200 rounded-xl p-8">
                    <div className="flex items-start gap-6">
                      <img
                        src={team.logo}
                        alt={team.name}
                        className="w-24 h-24 rounded-xl shadow-lg object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div 
                        className="w-24 h-24 rounded-xl bg-gradient-to-br from-orange-100 to-orange-200 flex items-center justify-center text-orange-700 font-bold text-2xl shadow-lg"
                        style={{ display: 'none' }}
                      >
                        {team.name?.charAt(0)?.toUpperCase() || 'T'}
                      </div>
                      <div className="flex-grow">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-1">{team.name}</h3>
                            <p className="text-orange-600 font-medium">@{team.uniqueName}</p>
                          </div>
                          <Link
                            to={`/talent-scout/get-team/${team._id}`}
                            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium"
                          >
                            View Team
                          </Link>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <p className="text-sm text-gray-500 font-medium">Sport</p>
                            <p className="text-gray-900 font-semibold">{team.sport}</p>
                          </div>
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <p className="text-sm text-gray-500 font-medium">Gender</p>
                            <p className="text-gray-900 font-semibold capitalize">{team.gender}</p>
                          </div>
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <p className="text-sm text-gray-500 font-medium">Type</p>
                            <p className="text-gray-900 font-semibold">{team.type}</p>
                          </div>
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <p className="text-sm text-gray-500 font-medium">Members</p>
                            <p className="text-gray-900 font-semibold">{team.members?.length || 0}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <p className="text-gray-500 text-lg">No team association</p>
                    <p className="text-gray-400 text-sm">This player is not currently associated with any team.</p>
                  </div>
                )}
              </div>
            )}

            {/* Organization Tab */}
            {activeTab === "organization" && (
              <div className="space-y-6">
                {organization ? (
                  <div className="bg-white border border-gray-200 rounded-xl p-8">
                    <div className="flex items-start gap-6">
                      <ProfileImage
                        image={organization.profilePicture}
                        alt={organization.userName}
                        className="w-24 h-24 rounded-xl shadow-lg"
                      />
                      <div className="flex-grow">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-1">
                              {organization.firstName} {organization.lastName}
                            </h3>
                            <p className="text-orange-600 font-medium">@{organization.userName}</p>
                          </div>
                          <Link
                            to={`/talent-scout/get-organization/${organization._id}`}
                            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium"
                          >
                            View Organization
                          </Link>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <p className="text-sm text-gray-500 font-medium">Email</p>
                            <p className="text-gray-900 font-semibold">{organization.email}</p>
                          </div>
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <p className="text-sm text-gray-500 font-medium">Phone</p>
                            <p className="text-gray-900 font-semibold">{organization.phone || "Not provided"}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <p className="text-gray-500 text-lg">No organization information</p>
                    <p className="text-gray-400 text-sm">Organization details are not available.</p>
                  </div>
                )}
              </div>
            )}

            {/* Parents Tab */}
            {activeTab === "parents" && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-semibold">Parent Guardians</h3>
                  {parents.length > 0 && (
                    <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                      {parents.length} parents
                    </span>
                  )}
                </div>

                {parentsLoading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading parents...</p>
                  </div>
                ) : parents.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-1a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    </div>
                    <p className="text-gray-500 text-lg">No parent information</p>
                    <p className="text-gray-400 text-sm">No parent guardians are associated with this player.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {parents.map((parent, index) => (
                      <div key={index} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                        <div className="flex items-center space-x-4">
                          <ProfileImage
                            image={parent.profilePicture}
                            alt={parent.userName}
                            className="w-16 h-16 rounded-lg object-cover ring-2 ring-purple-100"
                          />
                          <div className="flex-grow min-w-0">
                            <h4 className="font-semibold text-gray-900 truncate">
                              {parent.firstName} {parent.lastName}
                            </h4>
                            <p className="text-sm text-gray-500 truncate">@{parent.userName}</p>
                            <p className="text-xs text-purple-600 font-medium mt-1">Parent Guardian</p>
                            <p className="text-xs text-gray-400">{parent.email}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GetPlayer;
